/**
 * Done. CPD — Public Landing Page (Stitch v2)
 *
 * Single-scroll landing composing Hero / About / HowWeWork / StartCTA /
 * LearnLibrary as section anchors. Smooth-scrolled from PublicHeader.
 *
 * "Start" / "Get Started" CTAs trigger Stripe Checkout via the existing
 * /api/public/create-checkout endpoint. Authenticated users are redirected
 * to /dashboard before this page renders (handled in RouteGuard).
 */

import { useCallback, useState } from 'react';
import { api } from '@/lib/api';
import { PublicHeader } from '@/components/layout/PublicHeader';
import { PublicFooter } from '@/components/layout/PublicFooter';
import { Hero } from '@/components/landing/Hero';
import { About } from '@/components/landing/About';
import { HowWeWork } from '@/components/landing/HowWeWork';
import { StartCTA } from '@/components/landing/StartCTA';
import { LearnLibrary } from '@/components/landing/LearnLibrary';

export function LandingPage(): React.ReactElement {
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [checkoutError, setCheckoutError] = useState<string | null>(null);

  const startCheckout = useCallback(async (): Promise<void> => {
    if (checkoutLoading) return;
    setCheckoutLoading(true);
    setCheckoutError(null);
    try {
      const result = await api.post<{ url: string }>('/api/public/create-checkout', {});
      if (result.url) {
        window.location.href = result.url;
        return;
      }
      setCheckoutError('Checkout could not start. Please try again.');
    } catch {
      setCheckoutError('Checkout could not start. Please try again.');
    } finally {
      setCheckoutLoading(false);
    }
  }, [checkoutLoading]);

  const onStart   = (): void => { void startCheckout(); };
  const onExplore = (): void => {
    document.getElementById('section-learn')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div
      className="min-h-screen bg-[var(--stitch-surface)] text-[var(--stitch-on-surface)] stitch-font-body"
      id="main-content"
    >
      <PublicHeader onStart={onStart} />

      <main>
        <Hero onStart={onStart} onExplore={onExplore} />
        <About />
        <HowWeWork />
        <StartCTA onStart={onStart} />
        <LearnLibrary />
      </main>

      {checkoutLoading && (
        <div
          role="status"
          aria-live="polite"
          className="fixed bottom-6 right-6 z-[80] bg-[var(--stitch-primary)] text-[var(--stitch-on-primary)] px-5 py-3 rounded-full shadow-2xl stitch-font-label text-[13px] uppercase tracking-widest flex items-center gap-3"
        >
          <span className="material-symbols-outlined animate-spin">progress_activity</span>
          Redirecting to checkout…
        </div>
      )}

      {checkoutError && (
        <div
          role="alert"
          className="fixed bottom-6 right-6 z-[80] bg-white border border-[var(--stitch-outline-variant)] text-[var(--stitch-on-surface)] px-5 py-3 rounded-xl shadow-2xl text-sm max-w-sm"
        >
          {checkoutError}
        </div>
      )}

      <PublicFooter />
    </div>
  );
}
